import { useState, useEffect } from "react";
import type { AxiosInstance } from "axios";
import type { SdwanBranchDetailsResponse } from "../sdwan.types";
import { fetchSdwanBranchDetails } from "../requests/request";

/**
 * Load SD-WAN branch detail (Nivel 3):
 * GET /kontroledge/sdwan/:id/branches/:deviceName?tenantId=
 * Always served live from FortiManager (never persisted).
 */
export function useSdwanBranchDetails(
    sdwanId: string | null,
    branchRef: string | null,
    api: AxiosInstance,
    refresh: number,
    tenantId: number | null
) {
    const [raw, setRaw] = useState<SdwanBranchDetailsResponse | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (!sdwanId || !branchRef || tenantId == null) {
            setRaw(null);
            setLoading(false);
            setError(null);
            return;
        }

        let cancelled = false;
        setLoading(true);
        setError(null);

        (async () => {
            try {
                const response = await fetchSdwanBranchDetails(
                    api,
                    tenantId,
                    sdwanId,
                    branchRef
                );
                if (cancelled) return;
                const body = response.data;
                if (!body?.success || !body.data) {
                    throw new Error(body?.message ?? "Failed to load branch");
                }
                setRaw(body.data);
                setError(null);
            } catch (e) {
                console.error("Error in useSdwanBranchDetails:", e);
                if (!cancelled) {
                    const status = (e as { response?: { status?: number } })
                        ?.response?.status;
                    setRaw(null);
                    // 404 → branch not found (empty state); other failures
                    // (500 / FMG down / network) → error state with retry.
                    setError(
                        status === 404
                            ? null
                            : "We couldn't load this branch. Please try again."
                    );
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [sdwanId, branchRef, tenantId, api, refresh]);

    return { raw, loading, error };
}
